import { MouseEvent, useRef, useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ButtonGroup from "@mui/material/ButtonGroup";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import { SxProps } from "@mui/material/styles";
import { AccessRightChecks } from "../../../../features/user/accessRightChecks";
import { CustomButton } from "../customButton/CustomButton";

type SplitButtonOption = {
  id: string;
  label: string;
  disabled?: boolean;
  onClick: (
    event: MouseEvent<HTMLButtonElement | HTMLLIElement>,
    anchorEl: HTMLElement | null,
  ) => void;
};

type Props = {
  options: SplitButtonOption[];
  variant?: "text" | "outlined" | "contained";
  color?: "inherit" | "primary" | "secondary" | "success" | "error" | "info";
  size?: "small" | "medium" | "large";
  disabled?: boolean;
  accessRightChecks?: AccessRightChecks;
  sx?: SxProps;
  buttonSx?: SxProps;
  toggleButtonSx?: SxProps;
};

const ButtonSplit = ({
  options,
  variant = "outlined",
  color = "primary",
  size = "medium",
  disabled = false,
  accessRightChecks,
  sx,
  buttonSx,
  toggleButtonSx,
}: Props) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  if (!options.length) return null;

  const primaryOption = options[selectedIndex] || options[0];
  const menuOptions = options;

  const handlePrimaryClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (disabled || primaryOption.disabled) return;
    primaryOption.onClick(event, anchorRef.current);
  };

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prevOpen) => !prevOpen);
  };

  const handleMenuItemClick = (index: number) => {
    if (disabled || options[index]?.disabled) return;
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <ButtonGroup
        variant={variant}
        color={color}
        size={size}
        ref={anchorRef}
        sx={sx}
      >
        <CustomButton
          onClick={handlePrimaryClick}
          disabled={disabled || primaryOption.disabled}
          accessRightChecks={accessRightChecks}
          sx={buttonSx}
        >
          {primaryOption.label}
        </CustomButton>
        <CustomButton
          size="small"
          onClick={handleToggle}
          disabled={disabled}
          accessRightChecks={accessRightChecks}
          sx={toggleButtonSx ?? buttonSx}
        >
          <ArrowDropDownIcon />
        </CustomButton>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {menuOptions.map((option, index) => (
                    <MenuItem
                      key={option.id}
                      disabled={disabled || option.disabled}
                      selected={index === selectedIndex}
                      onClick={() => handleMenuItemClick(index)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default ButtonSplit;
